// deps
import React, { memo, useCallback, useEffect, useState } from "react";

// internal-deps
import { isEmpty, isEqual } from "@swiggy-private/js-utils";
import { format, replaceText } from "@swiggy-private/core-ui";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as ChevronDownIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDown/12";
import { default as ChevronRightIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/12";

// test-ids
import { TIP_WIDGET_TEST_IDS } from "@swiggy-private/payment-test-ids";

// constants
import { TIP_WIDGET_ANIMATION, TIP_WIDGET_TEXTS } from "@dineout-payment-success-ui/constants/TipWidget";

// styles
import {
    ChevronButton,
    PaymentOptionLabel,
    PaymentOptionTile,
    PaymentOptionsRow,
    SubtitleText,
    TipCtaButton,
    TipCtaWrap,
    TipWidgetCloudBackground,
    TipWidgetHeader,
    TipWidgetStickyContainer,
    TipWidgetTextBlock,
    TipWidgetWrapper,
    SingleOptionChevron,
    UpiTextWrapper,
} from "./styles";

// types
import { ITipPaymentOption, ITipWidgetProps } from "@dineout-payment-success-ui/interfaces";

const PAYMENT_ICON_DIMENSION = "30";
const ENTRANCE_FALLBACK_BUFFER_MS = 200;
const ENTRANCE_INTERACTIVE_FALLBACK_MS =
    TIP_WIDGET_TEXTS.LOAD_DELAY_MS + TIP_WIDGET_ANIMATION.SLIDE_UP_DURATION_MS + ENTRANCE_FALLBACK_BUFFER_MS;

const TipWidget: React.FC<ITipWidgetProps> = ({
    tipWidget,
    onPaymentOptionClick,
    onImpression,
    onCloseClick,
    onCtaClick,
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [hasEntranceStarted, setHasEntranceStarted] = useState(false);
    const [isInteractive, setIsInteractive] = useState(false);
    const isEnabled = !!tipWidget?.enabled;

    const handleEntranceStart = useCallback(
        (event: React.AnimationEvent<HTMLDivElement>) => {
            if (!isEqual(event.target, event.currentTarget)) {
                return;
            }
            setHasEntranceStarted(true);
            onImpression?.();
        },
        [onImpression],
    );

    const handleEntranceEnd = useCallback((event: React.AnimationEvent<HTMLDivElement>) => {
        if (!isEqual(event.target, event.currentTarget)) {
            return;
        }
        setIsFirstLoad(false);
        setIsInteractive(true);
    }, []);

    useEffect(() => {
        if (!isEnabled || isInteractive) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setIsFirstLoad(false);
            setIsInteractive(true);
        }, ENTRANCE_INTERACTIVE_FALLBACK_MS);

        return () => window.clearTimeout(timeoutId);
    }, [isEnabled, isInteractive]);

    const handleClose = useCallback(() => {
        setIsExpanded(false);
        onCloseClick?.();
    }, [onCloseClick]);

    const handleExpand = useCallback(() => {
        setIsExpanded(true);
        onCtaClick?.();
    }, [onCtaClick]);

    const handleOptionClick = useCallback(
        (option: ITipPaymentOption, position: number) => () => onPaymentOptionClick(option, position),
        [onPaymentOptionClick],
    );

    if (!isEnabled) {
        return null;
    }

    const { title, subtitle, paymentOptions } = tipWidget;
    const visiblePaymentOptions = paymentOptions.slice(0, TIP_WIDGET_TEXTS.MAX_PAYMENT_OPTIONS);
    const isSingleOption = visiblePaymentOptions.length === 1;

    return (
        <>
            <TipWidgetCloudBackground isExpanded={isExpanded} />
            <TipWidgetStickyContainer
                isInteractive={isInteractive}
                isExpanded={isExpanded}
                onAnimationStart={handleEntranceStart}
                onAnimationEnd={handleEntranceEnd}
                data-testid={TIP_WIDGET_TEST_IDS.CONTAINER}
            >
                <TipWidgetWrapper isExpanded={isExpanded}>
                    <TipWidgetHeader isExpanded={isExpanded}>
                        <TipWidgetTextBlock>
                            {!isEmpty(title) && (
                                <Text
                                    variant="V2.overline.neutral.medium"
                                    color="text_Low_Emphasis"
                                    data-testid={TIP_WIDGET_TEST_IDS.TITLE}
                                >
                                    {title}
                                </Text>
                            )}

                            {!isEmpty(subtitle) && (
                                <SubtitleText
                                    variant={isExpanded ? "V2.body2.neutral.bold" : "V2.body3.neutral.bold"}
                                    color="text_High_Emphasis"
                                    data-testid={TIP_WIDGET_TEST_IDS.SUBTITLE}
                                >
                                    {replaceText(subtitle, "", true)}
                                </SubtitleText>
                            )}
                        </TipWidgetTextBlock>

                        <ChevronButton
                            type="button"
                            isExpanded={isExpanded}
                            onClick={handleClose}
                            data-testid={TIP_WIDGET_TEST_IDS.CLOSE_BTN}
                            aria-label={TIP_WIDGET_TEXTS.CLOSE_LABEL}
                            tabIndex={isExpanded ? 0 : -1}
                            aria-hidden={!isExpanded}
                        >
                            <GenericIcon Icon={ChevronDownIcon} fillColor="text_Highest_Emphasis" />
                        </ChevronButton>
                    </TipWidgetHeader>

                    <TipCtaWrap isExpanded={isExpanded} aria-hidden={isExpanded}>
                        <TipCtaButton
                            variant="small"
                            buttonType="primary"
                            buttonSize="xSmall"
                            onClick={handleExpand}
                            data-testid={TIP_WIDGET_TEST_IDS.TIP_CTA}
                            tabIndex={isExpanded ? -1 : 0}
                        >
                            {TIP_WIDGET_TEXTS.TIP_CTA}
                        </TipCtaButton>
                    </TipCtaWrap>

                    {!isEmpty(paymentOptions) && (
                        <PaymentOptionsRow
                            isExpanded={isExpanded}
                            isFirstLoad={isFirstLoad && hasEntranceStarted}
                            aria-hidden={!isExpanded}
                        >
                            {visiblePaymentOptions.map((option: ITipPaymentOption, index) => {
                                const optionName = option.title;
                                const optionLabel = isSingleOption
                                    ? format(TIP_WIDGET_TEXTS.SINGLE_PAYMENT_OPTION_CTA, optionName)
                                    : optionName;
                                return (
                                    <PaymentOptionTile
                                        key={option.id}
                                        isExpanded={isExpanded}
                                        isSingleOption={isSingleOption}
                                        index={index}
                                        onClick={handleOptionClick(option, index + 1)}
                                        data-testid={`${TIP_WIDGET_TEST_IDS.PAYMENT_OPTION}-${option.id}`}
                                        tabIndex={isExpanded ? 0 : -1}
                                        aria-label={format(TIP_WIDGET_TEXTS.PAYMENT_OPTION_LABEL, optionName)}
                                    >
                                        <Image
                                            cloudinaryId={option.icon}
                                            alt=""
                                            width={PAYMENT_ICON_DIMENSION}
                                            height={PAYMENT_ICON_DIMENSION}
                                        />
                                        {isSingleOption ? (
                                            <UpiTextWrapper>
                                                <Text variant="V2.caption.neutral.medium" color="text_Med_Emphasis">
                                                    {optionLabel}
                                                </Text>
                                                <SingleOptionChevron
                                                    data-testid={TIP_WIDGET_TEST_IDS.SINGLE_OPTION_CHEVRON}
                                                >
                                                    <GenericIcon Icon={ChevronRightIcon} fillColor="text_Med_Emphasis" />
                                                </SingleOptionChevron>
                                            </UpiTextWrapper>
                                        ) : (
                                            <PaymentOptionLabel
                                                variant="V2.caption.neutral.medium"
                                                color="text_Med_Emphasis"
                                            >
                                                {optionLabel}
                                            </PaymentOptionLabel>
                                        )}
                                    </PaymentOptionTile>
                                );
                            })}
                        </PaymentOptionsRow>
                    )}
                </TipWidgetWrapper>
            </TipWidgetStickyContainer>
        </>
    );
};

export default memo(TipWidget);
